import React, { FC, memo, useCallback, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import moment from "moment";
import { useMediaQuery } from "../../hooks/common";

import PrivateRoutes from "./PrivateRoutes";
import AuthRoutes from "./AuthRoutes";
import { setSideNav } from "../../redux/recruiter/actions";
import {
  privateRoutes,
  routes,
  routesWithRedirect,
  subAuthRoutes,
} from "./routes";
import { User } from "../../core/entities/user";
import { logout, setLastIdle, verifyAuth } from "../../redux/auth/actions";
import { updateCurrentUserRequest } from "../../redux/user/action";
import { FETCH_USER } from "../../redux/user/actionType";
import SidebarWithHooks from "./SidebarWithHooks";
import { SidebarWrapper, Wrapper, SwitchWrapper } from "./styles";
import { PageNotFoundPage } from "../../components/pages/PageNotFoundPage";
import { MaintenancePage } from "../../components/pages/MaintenancePage";
import { data } from "../../constants";
import { theme } from "../../config";
import { messaging, getToken } from "../../firebase";

const RootNavigator: FC<{}> = () => {
  const history = useHistory();
  const location = useLocation();
  const locationPathname = location.pathname;
  const [showHideSidenav, setShowHideSidenav] = useState<string>("hidden");
  const [isTokenFound, setTokenFound] = useState(false);

  const isTabletOrMobile = useMediaQuery(theme.breakpoints.mobileAndTablet);

  const toggleSideNav = () => {
    let css = showHideSidenav === "hidden" ? "show" : "hidden";
    return setShowHideSidenav(css);
  };

  const isAuthenticated = useSelector(
    (state: RootStateOrAny) => state.auth.authenticated
  );

  const isLoading = useSelector(
    (state: RootStateOrAny) => state.auth.isLoading
  );

  const lastIdle = useSelector((state: RootStateOrAny) => state.auth.lastIdle);

  const request = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[FETCH_USER]
  );

  const user = useSelector((state: RootStateOrAny) => state.auth.user);

  const userAccessType = user && user.accessType;

  const dispatch = useDispatch();
  const dispatchSetSideNav = useCallback(
    (name: RootStateOrAny) => dispatch(setSideNav(name)),
    [dispatch]
  );

  const dispatchVerifyAuth = useCallback(() => dispatch(verifyAuth()), [
    dispatch,
  ]);

  const dispatchUpdateCurrentUser = useCallback(
    (id: string, params: Partial<User>) =>
      dispatch(updateCurrentUserRequest(id, params, "basicDetails")),
    [dispatch]
  );

  const dispatchLogout = useCallback(() => dispatch(logout()), [dispatch]);

  const dispatchSetIdletime = useCallback(
    (time: string) => dispatch(setLastIdle(time)),
    [dispatch]
  );

  const handleIdleLogout = () => {
    if (
      locationPathname.includes("/recruit") ||
      locationPathname.includes("/admin")
    ) {
      dispatchSetIdletime("");
      dispatchUpdateCurrentUser(user.id, { registrationToken: "" });
      dispatchLogout();
      return history.push("/login");
    }
  };

  const handleAction = () => {
    dispatchSetIdletime("");
  };

  useIdleTimer({
    //values: seconds in a hour * milliseconds in a second
    timeout:
      (lastIdle
        ? 3600 -
          Math.abs(
            moment(lastIdle).diff(
              moment(new Date()).format("MM/DD/YYYY hh:mm A"),
              "seconds"
            )
          )
        : 3600) * 1000,
    onIdle: handleIdleLogout,
    onActive: () => {},
    onAction: handleAction,
    debounce: 500,
  });

  const setIdleTime = () => {
    dispatchSetIdletime(moment().format("MM/DD/YYYY hh:mm A"));
  };

  useEffect(() => {
    window.addEventListener("unload", setIdleTime);

    if (
      lastIdle &&
      moment(lastIdle).diff(
        moment(new Date()).format("MM/DD/YYYY hh:mm A"),
        "hours"
      ) === -1
    ) {
      handleIdleLogout();
    }

    return () => {
      window.removeEventListener("unload", setIdleTime);
    };
  }, []);

  useEffect(() => {
    if (locationPathname === "/recruit") {
      dispatchSetSideNav(true);
    }

    dispatchVerifyAuth();
    if (
      userAccessType === "interviewer" &&
      data.noInterviewerRoutes.some((routePath) =>
        location.pathname.includes(routePath)
      )
    ) {
      history.replace("/recruit/initial-interview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchSetSideNav, dispatchVerifyAuth, locationPathname]);

  useEffect(() => {
    if (messaging && isAuthenticated) {
      getToken(messaging, { vapidKey: process.env.REACT_APP_MESSAGING_API_KEY })
        .then((currentToken: any) => {
          if (currentToken) {
            setTokenFound(true);
            // Track the token -> client mapping, by sending to backend server
            // show on the UI that permission is secured
            dispatchUpdateCurrentUser(user.id, { registrationToken: currentToken });
          } else {
            setTokenFound(false);
            // shows on the UI that permission is required
          }
        })
        .catch((err: any) => {
          console.log("An error occurred while retrieving token. ", err);
          // catch error while creating client token
        });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const routes = [
      "/recruit/initial-interview/",
      "/recruit/applicants-database",
      "/recruit/final-interview/",
      "/recruit/job-offer/",
    ];
    if (routes.some((route) => location.pathname.includes(route))) {
      require("../../assets/css/dropdown.css");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isTabletOrMobile) {
      setShowHideSidenav("hidden");
    }
  }, [isTabletOrMobile]);

  const hostName = window.location.hostname;
  const pathName = window.location.pathname;
  useEffect(() => {
    const applicantRestrictedRoutes = [...routes.map((route) => route.path)];
    if (
      hostName.includes("careers") &&
      applicantRestrictedRoutes.includes(pathName)
    ) {
      return history.push("/");
    } else if (
      hostName.includes("evrecruit") &&
      pathName.includes("/careers")
    ) {
      return history.push("/");
    } else if (
      hostName.includes("evrecruit") &&
      pathName.includes("/careers")
    ) {
      return history.push("/");
    } else if (
      hostName.includes("evrecruit") &&
      pathName.includes("/careers")
    ) {
      return history.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname, window.location.hostname]);

  const onClickWrapper = () => {
    if (showHideSidenav === "show" && isTabletOrMobile) {
      setShowHideSidenav("hidden");
    }
  };

  return (
    <Wrapper showHideSidenav={showHideSidenav}>
      {isAuthenticated &&
        (locationPathname.includes("/recruit") ||
          locationPathname.includes("/admin")) && ( // This is showing sidebar... NOTE: You can change this permission into ifAuthenticated() / authentication conditions upon logging in
          <SidebarWrapper>
            <SidebarWithHooks
              currentSubmenu={
                locationPathname.includes("/recruit")
                  ? "Recruitment"
                  : "Settings"
              }
              user={user}
              toggleSideNav={toggleSideNav}
              showHideSidenav={showHideSidenav}
              defaultSelectedKey={locationPathname}
            />
          </SidebarWrapper>
        )}
      <SwitchWrapper onClick={onClickWrapper}>
        <Switch>
          {isAuthenticated &&
          !isLoading &&
          user &&
          user.accessType &&
          !request?.isLoading &&
          request?.didSucceed ? (
            userAccessType === "interviewer" ? (
              <Redirect exact from="/" to={"/recruit/initial-interview"} />
            ) : (
              <Redirect exact from="/" to={"/recruit/new-applicants"} />
            )
          ) : (
            <Redirect
              exact
              from="/"
              to={hostName.includes("careers") ? "/careers" : "/login"}
            />
          )}
          {routesWithRedirect.map((route) => (
            <Route key={route.path} exact path={route.path}>
              <Redirect to={route.to} />
            </Route>
          ))}
          {subAuthRoutes.map((route) => (
            <Route
              key={route.path}
              exact
              path={route.path}
              component={route.component}
            />
          ))}
          {routes.map((route) => (
            <AuthRoutes
              isInterviewer={
                request?.response &&
                request?.response.accessType === "interviewer"
              }
              isLoggedIn={
                request?.response &&
                isAuthenticated &&
                !isLoading &&
                user &&
                !request?.isLoading
              }
              key={route.path}
              exact
              path={route.path}
              component={route.component}
            />
          ))}
          {privateRoutes.map((route) => (
            <PrivateRoutes
              isLoggedIn={isAuthenticated}
              key={route.path}
              exact
              path={route.path}
              component={route.component}
            />
          ))}
          <Route key="*" exact component={PageNotFoundPage} />
        </Switch>
      </SwitchWrapper>
    </Wrapper>
  );
};

export default memo(RootNavigator);
